import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { formatDate, statusLabel } from "@/lib/helpers";
import { Printer } from "lucide-react";

/**
 * Classroom Roster — printable, paper-optimized layout.
 *
 * Deliberately rendered as its own top-level route (outside
 * DashboardLayout, no sidebar/topbar/AI widget) so instructors can print a
 * clean, black-and-white-friendly attendance sheet to carry on a
 * clipboard. See App.css for the accompanying `@media print` rules.
 */
export default function ClassroomRosterPrintPage() {
  const { classroomId } = useParams();
  const [roster, setRoster] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/classrooms/${classroomId}/roster`)
      .then((res) => setRoster(res.data))
      .catch((err) => setError(err?.response?.data?.detail || "Could not load roster"));
  }, [classroomId]);

  if (error) {
    return <div className="p-8 text-center text-sm text-muted-foreground">{error}</div>;
  }
  if (!roster) {
    return <div className="p-8 text-center text-sm text-muted-foreground">Loading roster...</div>;
  }

  return (
    <div className="max-w-3xl mx-auto p-8 print:p-0 bg-white text-black" data-testid="classroom-roster-print-page">
      <div className="flex items-center justify-between mb-6 print:hidden">
        <p className="text-sm text-muted-foreground">Printable roster — use your browser&apos;s print dialog.</p>
        <Button onClick={() => window.print()} data-testid="roster-print-trigger-button">
          <Printer className="h-4 w-4 mr-2" /> Print
        </Button>
      </div>

      <div className="border-b-2 border-black pb-3 mb-4">
        <h1 className="text-xl font-semibold">{roster.class_name}</h1>
        <p className="text-sm mt-1">
          {(roster.dates || []).map((d) => formatDate(d)).join(", ")} &middot; {roster.start_time}-{roster.end_time} &middot; {roster.location || "-"}
        </p>
        <p className="text-sm">Instructor: {roster.instructor_name || "Unassigned"}</p>
      </div>

      <table className="w-full text-sm border-collapse" data-testid="roster-print-table">
        <thead>
          <tr className="border-b border-black">
            <th className="text-left py-2 pr-2 font-semibold">Student Name</th>
            <th className="text-left py-2 pr-2 font-semibold w-32">Attendance</th>
            <th className="text-left py-2 font-semibold">Notes</th>
          </tr>
        </thead>
        <tbody>
          {roster.students.map((s) => (
            <tr key={s.student_id} className="border-b border-zinc-300">
              <td className="py-3 pr-2 align-top">{s.full_name}</td>
              <td className="py-3 pr-2 align-top">{s.attendance_status ? statusLabel(s.attendance_status) : "\u2610 Not Recorded"}</td>
              <td className="py-3 align-top">
                <div className="h-6 border-b border-dotted border-zinc-400" />
              </td>
            </tr>
          ))}
          {roster.students.length === 0 && (
            <tr>
              <td colSpan={3} className="py-6 text-center text-muted-foreground">No students enrolled.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
